import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Home as HomeIcon, Heart, Calendar, Coins, Sparkles, Bell, Bookmark, FileText, RefreshCw } from "lucide-react";
import PropertyCard from "@/components/PropertyCard";
import { useAuth } from "@/context/AuthContext";

const eur = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const INT_STATUS = { new: "Nieuw", contacted: "Contact opgenomen", viewing_scheduled: "Bezichtiging gepland", not_interested: "Geen interesse", converted_to_offer: "Bod uitgebracht", closed: "Afgesloten" };
const VW_STATUS = { requested: "Aangevraagd", confirmed: "Bevestigd", rescheduled: "Verzet", completed: "Uitgevoerd", no_show: "Niet verschenen", cancelled: "Geannuleerd" };
const OF_STATUS = { submitted: "Ingediend", communicated_to_seller: "Doorgegeven aan verkoper", counter_offer: "Tegenbod", accepted: "Geaccepteerd", rejected: "Afgewezen", withdrawn: "Ingetrokken", sold: "Verkocht" };
const CONTINGENCY_LABELS = { bouwkundige_keuring: "Bouwkundige keuring", taxatie: "Taxatie", verkoop_eigen_woning: "Verkoop eigen woning", financiering: "Financiering", nvm_akte: "NVM koopakte" };

const TABS = [
  { k: "overview", label: "Overzicht", icon: HomeIcon },
  { k: "matches", label: "Matches", icon: Sparkles },
  { k: "saved", label: "Opgeslagen", icon: Bookmark },
  { k: "interests", label: "Interesses", icon: Heart },
  { k: "viewings", label: "Bezichtigingen", icon: Calendar },
  { k: "offers", label: "Biedingen", icon: Coins },
  { k: "alerts", label: "Alerts", icon: Bell },
];

export default function MyWoonVerder() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [emailInput, setEmailInput] = useState(user?.email || "");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (em) => {
    setBusy(true);
    try {
      const params = em ? { email: em } : {};
      const { data } = await http.get("/my/dashboard", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (user?.email) load(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submitLookup = (e) => { e.preventDefault(); if (emailInput) load(emailInput); };

  const counts = {
    matches: data?.matches?.length || 0, saved: data?.saved?.length || 0,
    interests: data?.interests?.length || 0, viewings: data?.viewings?.length || 0,
    offers: data?.offers?.length || 0,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">Persoonlijk portaal</p>
      <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--wv-obsidian)] mt-2" data-testid="mine-title">Mijn Woon Verder</h1>

      {!user && (
        <form onSubmit={submitLookup} className="mt-6 p-5 bg-white border border-slate-200 rounded-2xl flex flex-wrap items-end gap-3" data-testid="mine-lookup">
          <div className="flex-1 min-w-[220px]">
            <label className="text-sm font-semibold">Voer uw email in om uw portaal te openen</label>
            <input className="wv-input mt-1" type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} required />
          </div>
          <button className="wv-btn-primary" data-testid="mine-lookup-btn">Openen</button>
          <Link to="/login" className="text-sm text-slate-500 hover:text-slate-800">of log in</Link>
        </form>
      )}

      {/* Tabs */}
      <div className="mt-8 flex gap-2 flex-wrap border-b border-slate-200 pb-2 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 ${tab === t.k ? "bg-[color:var(--wv-navy)] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`mine-tab-${t.k}`}>
            <t.icon className="w-4 h-4" /> {t.label}
            {t.k !== "overview" && t.k !== "alerts" && counts[t.k] > 0 && <span className="ml-1 text-xs bg-white/20 px-1.5 py-0.5 rounded-full">{counts[t.k]}</span>}
          </button>
        ))}
        <button onClick={() => load(emailInput || user?.email)} className="ml-auto px-3 py-2 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-50 inline-flex items-center gap-1" data-testid="mine-refresh"><RefreshCw className="w-3.5 h-3.5" /> Ververs</button>
      </div>

      {busy && <p className="mt-6 text-slate-500">Laden…</p>}

      {!busy && tab === "overview" && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {TABS.slice(1, 6).map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} className="p-5 bg-white border border-slate-200 rounded-2xl text-left hover:border-slate-300" data-testid={`mine-overview-${t.k}`}>
              <t.icon className="w-5 h-5 text-[color:var(--wv-green)]" />
              <p className="mt-2 text-xs uppercase font-bold tracking-wider text-slate-500">{t.label}</p>
              <p className="mt-1 font-mono-num text-3xl font-extrabold">{counts[t.k]}</p>
            </button>
          ))}
        </div>
      )}

      {!busy && tab === "matches" && (
        <div className="mt-6">
          {data?.matches?.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="mine-matches-grid">
              {data.matches.map((m) => <PropertyCard key={m.property.id} p={m.property} matchScore={m.score} />)}
            </div>
          ) : (
            <p className="text-slate-500">Nog geen matches. Maak eerst een <Link to="/woning-zoeken" className="text-[color:var(--wv-navy)] font-semibold hover:underline">zoekprofiel</Link>.</p>
          )}
        </div>
      )}

      {!busy && tab === "saved" && (
        <div className="mt-6">
          {data?.saved?.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.saved.map((p) => <PropertyCard key={p.id} p={p} />)}</div>
          ) : <p className="text-slate-500">Nog geen opgeslagen woningen.</p>}
        </div>
      )}

      {!busy && tab === "interests" && (
        <div className="mt-6 space-y-3">
          {data?.interests?.length ? data.interests.map((i) => (
            <div key={i.id} className="p-4 rounded-xl bg-white border border-slate-200 flex flex-wrap justify-between gap-3">
              <div><p className="font-semibold">{i.property_title}</p><p className="text-xs text-slate-500 font-mono-num">{new Date(i.created_at).toLocaleDateString("nl-NL")}</p></div>
              <p className="wv-status-pill wv-status-review">{INT_STATUS[i.status] || i.status}</p>
            </div>
          )) : <p className="text-slate-500">Nog geen interesses.</p>}
        </div>
      )}

      {!busy && tab === "viewings" && (
        <div className="mt-6 space-y-3">
          {data?.viewings?.length ? data.viewings.map((v) => (
            <div key={v.id} className="p-4 rounded-xl bg-white border border-slate-200 flex flex-wrap justify-between gap-3">
              <div><p className="font-semibold">{v.property_title}</p><p className="text-xs text-slate-500">Voorkeur: {v.preferred_date} ({v.preferred_time})</p></div>
              <p className="wv-status-pill wv-status-review">{VW_STATUS[v.status] || v.status}</p>
            </div>
          )) : <p className="text-slate-500">Nog geen bezichtigingen aangevraagd.</p>}
        </div>
      )}

      {!busy && tab === "offers" && (
        <div className="mt-6 space-y-3">
          {data?.offers?.length ? data.offers.map((o) => (
            <div key={o.id} className="p-4 rounded-xl bg-white border border-slate-200" data-testid={`mine-offer-${o.id}`}>
              <div className="flex flex-wrap justify-between gap-3 items-start">
                <div className="flex-1 min-w-[220px]">
                  <p className="font-semibold">{o.property_title}</p>
                  <p className="text-xs text-slate-500 font-mono-num">{new Date(o.created_at).toLocaleDateString("nl-NL")} · Bod-ID {o.id?.slice(0, 8).toUpperCase()}</p>
                  <p className="mt-2 text-sm">Uw bod: <span className="font-mono-num font-bold text-slate-900">{eur(o.amount)}</span>{o.asking_price ? <span className="text-slate-500"> · vraagprijs {eur(o.asking_price)}</span> : null}</p>
                  <div className="mt-2 grid sm:grid-cols-2 gap-1 text-xs text-slate-600">
                    <div>Financiering: <span className="font-semibold text-slate-800">{o.financing}</span>{o.financing_reservation ? ` · voorbehoud ${o.financing_reservation_days || 42} dgn` : ""}</div>
                    {o.deposit_percent != null && <div>Waarborg: <span className="font-semibold text-slate-800 font-mono-num">{o.deposit_percent}%</span></div>}
                    {o.transfer_date && <div>Overdracht: <span className="font-semibold text-slate-800">{o.transfer_date}</span></div>}
                    {o.valid_until && <div>Geldig tot: <span className="font-semibold text-slate-800">{o.valid_until}</span></div>}
                  </div>
                  {o.contingencies?.length > 0 && (
                    <p className="mt-2 text-xs text-slate-600">Voorwaarden: {o.contingencies.map((c) => CONTINGENCY_LABELS[c] || c).join(" · ")}</p>
                  )}
                  {o.message_to_seller && <p className="mt-2 text-xs text-slate-600 italic">"{o.message_to_seller}"</p>}
                </div>
                <p className="wv-status-pill wv-status-review whitespace-nowrap">{OF_STATUS[o.status] || o.status}</p>
              </div>
              {o.activities?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Tijdlijn</p>
                  <ul className="space-y-1">
                    {o.activities.slice().reverse().map((a, i) => (
                      <li key={i} className="text-xs text-slate-600 flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--wv-green)] mt-1.5 flex-shrink-0" />
                        <div><span className="font-mono-num text-slate-400">{new Date(a.at).toLocaleString("nl-NL")}</span> — <span className="font-semibold text-slate-700">{OF_STATUS[a.new_status] || a.new_status}</span>{a.note ? ` · ${a.note}` : ""}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )) : <p className="text-slate-500">Nog geen biedingen uitgebracht.</p>}
        </div>
      )}

      {!busy && tab === "alerts" && (
        <div className="mt-6 p-6 bg-white border border-slate-200 rounded-2xl">
          <p className="font-display font-bold text-lg">Zoekopdracht alerts</p>
          <p className="mt-2 text-slate-600">Uw huidige alert-frequentie: <span className="font-semibold text-[color:var(--wv-navy)]">{data?.profile?.alert_frequency || "niet ingesteld"}</span></p>
          <Link to="/woning-zoeken" className="mt-4 inline-flex wv-btn-outline text-sm" data-testid="mine-alerts-edit">Zoekprofiel aanpassen</Link>
        </div>
      )}
    </div>
  );
}
